import { ctaCopy, type CtaKey } from '../config/cta-copy.js';
import { brand, customizations, tornadoActive, weather } from '../state/app-state.js';
import type { BrandBrief, SlotId } from '../types/manifest.js';
import { CtaBar } from './overlay/cta-bar.js';
import { LogoWatermark } from './overlay/logo-watermark.js';
import { NewsCaption } from './overlay/news-caption.js';
import { TornadoBanner } from './overlay/tornado-banner.js';
import { WeatherOverlay } from './overlay/weather-overlay.js';
import { RotationEngine, type RotationItem } from './rotation-engine.js';
import { SignRenderer } from './sign-renderer.js';

export class SignController {
  readonly renderer: SignRenderer;
  readonly engine: RotationEngine;
  private logo: LogoWatermark;
  private cta: CtaBar;
  private newsCaption: NewsCaption;
  private weatherOverlay: WeatherOverlay;
  private tornado: TornadoBanner;

  constructor(parent: HTMLElement) {
    this.renderer = new SignRenderer(parent);
    const stage = this.renderer.element();
    this.weatherOverlay = new WeatherOverlay(stage);
    this.tornado = new TornadoBanner(stage);
    this.cta = new CtaBar(stage);
    this.newsCaption = new NewsCaption(stage);
    this.logo = new LogoWatermark(stage);
    this.engine = new RotationEngine(this.renderer);
    this.engine.onItemChange = (item) => this.applyChromeFor(item);
    weather.subscribe((mode) => {
      if (!tornadoActive.get()) this.weatherOverlay.set(mode);
    });
    tornadoActive.subscribe((active) => {
      if (active) {
        this.weatherOverlay.set('sunny');
        this.tornado.setText(customizations.get().tornadoBannerText ?? '');
        this.tornado.show();
      } else {
        this.tornado.hide();
        this.weatherOverlay.set(weather.get());
      }
    });
    brand.subscribe((b) => this.applyPalette(b));
  }

  private applyPalette(b: BrandBrief | null): void {
    const stage = this.renderer.element();
    if (!b) {
      stage.style.removeProperty('--cta-bg');
      stage.style.removeProperty('--cta-text');
      stage.style.removeProperty('--cta-pill-bg');
      stage.style.removeProperty('--cta-pill-text');
      return;
    }
    stage.style.setProperty('--cta-bg', b.palette.primary);
    stage.style.setProperty('--cta-text', b.palette.text);
    stage.style.setProperty('--cta-pill-bg', b.palette.accent);
    stage.style.setProperty('--cta-pill-text', b.palette.ctaText);
  }

  setLogo(url: string): void { this.logo.setUrl(url); }

  /** Force re-apply chrome from current signal state (used after a customization commit). */
  refreshChrome(): void {
    if (tornadoActive.get()) {
      this.tornado.setText(customizations.get().tornadoBannerText ?? '');
      this.tornado.show();
    }
  }

  applyChromeFor(item: RotationItem | null): void {
    if (!item) { this.cta.hide(); this.newsCaption.hide(); return; }
    if (item.id === 'triggered.tornado') { this.cta.hide(); this.newsCaption.hide(); return; }
    if (item.source === 'news') {
      this.cta.hide();
      if (item.caption) this.newsCaption.set(item.caption); else this.newsCaption.hide();
      return;
    }
    this.newsCaption.hide();

    // Customization wins; fall back to built-in ctaCopy.
    const custom = customizations.get().slots[item.id as SlotId];
    const builtin = (item.id in ctaCopy) ? ctaCopy[item.id as CtaKey] : null;
    const message = custom?.message ?? builtin?.ad ?? '';
    const cta = custom?.cta ?? builtin?.cta ?? '';
    if (message || cta) this.cta.set(message, cta);
    else this.cta.hide();
  }
}
